import classNames from "classnames";
import { MmOrder, MmOrderType } from "../types/mm-order.type";
import WidgetList from "../../dashboard/components/WidgetList";
import { InboxOutlined } from "@mui/icons-material";
import { formatNumberWithSeparator } from "../../../utils/number-utils";
import { CircularProgress, Slider } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

interface IProps {
  type: MmOrderType;
  orders: MmOrder[];
  isLoading: boolean;
}

const DECIMALS = 5;

const renderHeader = (type: MmOrderType) => (
  <div className="flex items-center gap-10">
    <div
      className={classNames("flex-1 text-center flex items-center pl-2 pr-4", {
        "order-1": type === MmOrderType.Sell,
        "order-4": type === MmOrderType.Buy,
      })}
    >
      <span
        className={classNames("flex-1 text-xxs opacity-75", {
          "order-1 text-left": type === MmOrderType.Sell,
          "order-3 text-right pr-1": type === MmOrderType.Buy,
        })}
      >
        MM Order
      </span>

      <span className="order-2 flex-[0_0_80px] text-right">
        MM Amount(Exim)
      </span>

      <span
        className={classNames("flex-1 text-xxs opacity-75", {
          "order-3 text-right": type === MmOrderType.Sell,
          "order-1 text-left": type === MmOrderType.Buy,
        })}
      >
        Organic
      </span>
    </div>

    <div
      className={classNames("flex-[0_0_100px]", {
        "text-right order-2": type === MmOrderType.Sell,
        "text-left order-3": type === MmOrderType.Buy,
      })}
    >
      Total(USDT)
    </div>

    <div
      className={classNames("flex-[0_0_100px]", {
        "text-right order-3": type === MmOrderType.Sell,
        "text-left order-2": type === MmOrderType.Buy,
      })}
    >
      Amount(EXIM)
    </div>

    <div
      className={classNames("flex-[0_0_100px]", {
        "order-4 pr-4 text-right": type === MmOrderType.Sell,
        "order-1 pl-4 text-left": type === MmOrderType.Buy,
      })}
    >
      Price(USDT)
    </div>
  </div>
);

const renderItem = (
  order: Partial<MmOrder>,
  type: MmOrderType,
  isTotalRow?: boolean
) => {
  const percent = order.amount
    ? `${Math.max(
        0,
        100 - (order.myAmount / (order.amount || 1)) * 100
      ).toFixed(2)}`
    : "0";

  return (
    <div className="flex items-center gap-10 text-sm">
      <div
        className={classNames("flex-1 text-center", {
          "order-1": type === MmOrderType.Sell,
          "order-4": type === MmOrderType.Buy,
        })}
      >
        {isTotalRow ? null : (
          <div
            className={classNames(
              "relative border mx-2 flex items-center gap-4 px-2 rounded",
              {
                "border-red-mm": type === MmOrderType.Sell,
                "border-green-mm": type === MmOrderType.Buy,
              }
            )}
          >
            <div
              className={classNames("absolute top-0 left-0 h-full opacity-25", {
                "bg-red-mm": type === MmOrderType.Sell,
                "bg-green-mm": type === MmOrderType.Buy,
              })}
              style={{
                width: `${percent}%`,
              }}
            />
            <span
              className={classNames("flex-1 text-xs opacity-75", {
                "order-1 text-left": type === MmOrderType.Sell,
                "order-3 text-right pr-1": type === MmOrderType.Buy,
              })}
            >
              ({order.count})
            </span>

            <span className="order-2 flex-[0_0_70px] text-right font-medium">
              {formatNumberWithSeparator(order.myAmount, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>

            <span
              className={classNames("flex-1 text-xs opacity-75", {
                "order-3 text-right": type === MmOrderType.Sell,
                "order-1 text-left": type === MmOrderType.Buy,
              })}
            >
              {formatNumberWithSeparator(percent)}%
            </span>
          </div>
        )}
      </div>

      <div
        className={classNames("flex-[0_0_100px]", {
          "text-right order-2": type === MmOrderType.Sell,
          "text-left order-3": type === MmOrderType.Buy,
        })}
      >
        {formatNumberWithSeparator(order.total, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </div>

      <div
        className={classNames("flex-[0_0_100px]", {
          "text-right order-3": type === MmOrderType.Sell,
          "text-left order-2": type === MmOrderType.Buy,
        })}
      >
        {isTotalRow
          ? null
          : formatNumberWithSeparator(order.amount, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
      </div>

      <div
        className={classNames("flex-[0_0_100px] font-semibold", {
          "order-4 pr-4 text-right text-sell": type === MmOrderType.Sell,
          "order-1 pl-4 text-left text-buy": type === MmOrderType.Buy,
        })}
      >
        {isTotalRow
          ? null
          : formatNumberWithSeparator(order.price, {
              minimumFractionDigits: DECIMALS,
              maximumFractionDigits: DECIMALS,
            })}
      </div>
    </div>
  );
};

export default function CexOrdersList({ type, orders, isLoading }: IProps) {
  const [value, setValue] = useState<number[]>([0, 0]);

  const min = useMemo(() => {
    return Number(
      (
        (type === MmOrderType.Sell
          ? orders?.[0]?.price || 0
          : orders?.[orders.length - 1]?.price || 0) * Math.pow(10, DECIMALS)
      ).toFixed(0)
    );
  }, [orders, type]);

  const max = useMemo(() => {
    return Number(
      (
        (type === MmOrderType.Sell
          ? orders?.[orders.length - 1]?.price || 0
          : orders?.[0]?.price || 0) * Math.pow(10, DECIMALS)
      ).toFixed(0)
    );
  }, [orders, type]);

  useEffect(() => {
    setValue([min, max]);
  }, [min, max]);

  const totalSelected = useMemo(() => {
    return orders
      .filter(
        (order) =>
          order.price >= value[0] / Math.pow(10, DECIMALS) &&
          order.price <= value[1] / Math.pow(10, DECIMALS)
      )
      .reduce((acc, order) => acc + order.total, 0);
  }, [orders, value]);

  return (
    <div className="relative flex-1">
      <h1 className="px-2 text-center uppercase border-b border-dashed">
        {type}
      </h1>
      <WidgetList
        itemsContainerClassName="max-h-[370px] overflow-y-auto"
        items={orders}
        getKey={(order) => order.price}
        renderHeader={() => renderHeader(type)}
        renderItem={(order) => renderItem(order, type)}
        renderEmpty={() =>
          isLoading ? (
            <div className="mt-24 w-full flex justify-center">
              <CircularProgress size={20} />
            </div>
          ) : (
            <div className="mt-24 w-full flex justify-center gap-1 opacity-75">
              <InboxOutlined /> No orders found
            </div>
          )
        }
      />

      <div className="absolute bottom-0 inset-x-0 px-8 h-12 bg-bg">
        <Slider
          className="-mb-3"
          min={min}
          max={max}
          marks={orders.map((order) => ({
            value: order.price * Math.pow(10, DECIMALS),
          }))}
          valueLabelDisplay="auto"
          value={value}
          onChange={(_, newValue) => setValue(newValue as number[])}
          size="small"
          valueLabelFormat={(value) => `${value / Math.pow(10, DECIMALS)}`}
        />

        {renderItem({ total: totalSelected }, type, true)}
      </div>
    </div>
  );
}
